import { get, isEmpty } from 'radash';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';

type BindingSnapshot = {
    attribute: any;
    scope: any;
    fieldPath: string;
};

interface AngularJSTemplateRendererProps {
    template: string;
    attribute: any;
    fieldPath?: string;
    scope?: any; // Additional scope data
    className?: string;
}

/** Resolve scope.item for templates: use parent object when fieldPath is nested (e.g. group.0.simple_external_image) so item[a.name] binds correctly. */
function getScopeItem(fullItem: any, fieldPath: string): any {
    if (!fullItem) return fullItem;
    const segments = fieldPath?.split?.('.') ?? [];
    if (segments.length <= 1) return fullItem;
    const parentPath = segments.slice(0, -1).join('.');
    const context = get(fullItem, parentPath);
    return context !== undefined ? context : fullItem;
}

/**
 * Renders attributes that use a custom AngularJS template as their renderer.
 *
 * In the data model, an attribute can specify a custom renderer (see demomodel.rb for 'CustomExternalImage',
 * 'Movies', 'CustomSimpleCopiedString'...) whose template is an AngularJS HTML snippet (see demomodel-customTemplates.html).
 * This component bridges React Hook Form → AngularJS for such snippets.
 */
const AngularJSTemplateRenderer = ({
    template,
    attribute,
    fieldPath = attribute?.name,
    scope = {},
    className
}: AngularJSTemplateRendererProps) => {

    const resolvedFieldPath = fieldPath ?? attribute?.name ?? '';

    const { setValue, getValues, watch } = useFormContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const scopeRef = useRef<any>(null);
    const currentFormItemRef = useRef<any>(null);

    const bindingRef = useRef<BindingSnapshot>({
        attribute,
        scope,
        fieldPath: resolvedFieldPath,
    });
    bindingRef.current = { attribute, scope, fieldPath: resolvedFieldPath };

    const [rerenderKey, setRerenderKey] = useState(0);

    const $angularJsInjector: any = getInjector();
    const $rootScope = getRootScope($angularJsInjector);
    const $compile = $angularJsInjector.get('$compile');
    const $rootScopeRef = useRef($rootScope);
    $rootScopeRef.current = $rootScope;

    // Subscribe imperatively to field value changes to sync AngularJS scope without causing re-renders.
    // We sync $scope.item on ANY field change (not just resolvedFieldPath) because AngularJS templates
    // can read any field on $scope.item — e.g. In demomodel.rb the CustomSimpleCopiedString template
    useEffect(() => {
        const { unsubscribe } = watch((_, { name }) => {
            const formValues = getValues();

            // Only trigger a template recompile when this specific field becomes empty
            if (name === resolvedFieldPath || name === undefined) {
                if (isEmpty(get(formValues, resolvedFieldPath))) {
                    currentFormItemRef.current = formValues;
                    setRerenderKey((k) => k + 1);
                }
            }

            // Always push the latest form values to $scope.item so sibling fields stay current
            const $scope = scopeRef.current;
            if (!$scope || !containerRef.current) return;

            const { attribute: a, scope: s, fieldPath: fp } = bindingRef.current;
            $scope.item = getScopeItem(formValues, fp);
            $scope.a = { ...a, readonly: s.readonly };
            Object.assign($scope, s);
            $rootScopeRef.current.$evalAsync();
        });
        return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedFieldPath]);

    // Sync AngularJS scope when attribute or scope props change
    useEffect(() => {
        const $scope = scopeRef.current;
        if (!$scope || !containerRef.current) return;

        const formValues = getValues();
        const { fieldPath: fp } = bindingRef.current;
        $scope.item = getScopeItem(formValues, fp);
        $scope.a = { ...attribute, readonly: scope.readonly };
        Object.assign($scope, scope);
        $rootScopeRef.current.$evalAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attribute, scope]);

    // Compile template (and recompile when rerenderKey changes so Angular sees cleared attribute)
    useEffect(() => {
        if (!containerRef.current || !template) return;

        const currentElement = containerRef.current;
        let $scope: any = null;
        let cancelled = false;

        const compileTemplate = async () => {
            try {

                $scope = $rootScope.$new();
                if (cancelled) {
                    $scope.$destroy();
                    return;
                }
                scopeRef.current = $scope;

                const { attribute: a, scope: s, fieldPath: fp } = bindingRef.current;

                $scope.item = getScopeItem(getValues(), fp);
                $scope.a = { ...a, readonly: s.readonly };

                // Add any additional scope data
                Object.assign($scope, s);

                $scope.$on('itemChanged', (_: any, newItem: any) => {
                    const { attribute: attr, fieldPath: path } = bindingRef.current;
                    const newValue = newItem[attr.name];

                    setValue(path ?? attr.name, newValue, {
                        shouldDirty: true
                    });
                });

                // Compile the template and attach the $scope to it
                if (cancelled) {
                    $scope.$destroy();
                    scopeRef.current = null;
                    return;
                }

                const compileResult = $compile(template)($scope);

                // Clear previous content and append new content
                currentElement.innerHTML = '';
                //@ts-ignore
                angular.element(currentElement).append(compileResult);

                $rootScope.$evalAsync();

                // Add Tailwind classes to option spans and inputs after template is rendered
                setTimeout(() => {
                    // Add tw-text-t-base and tw-text-t-primary to option-label spans
                    const optionLabelSpans = currentElement.querySelectorAll('span.option-label');
                    optionLabelSpans.forEach((span: Element) => {
                        if (!span.classList.contains('tw-text-t-base')) {
                            span.classList.add('tw-text-t-base');
                        }
                        if (!span.classList.contains('tw-text-t-primary')) {
                            span.classList.add('tw-text-t-primary');
                        }
                    });
                    // Add tw-text-t-sm and tw-text-t-primary to option-key spans
                    const optionKeySpans = currentElement.querySelectorAll('span.option-key');
                    optionKeySpans.forEach((span: Element) => {
                        if (!span.classList.contains('tw-text-t-sm')) {
                            span.classList.add('tw-text-t-sm');
                        }
                        if (!span.classList.contains('tw-text-t-primary')) {
                            span.classList.add('tw-text-t-primary');
                        }
                    });
                    // Add tw-text-t-base and tw-text-t-primary to all input elements
                    const inputs = currentElement.querySelectorAll('input');
                    inputs.forEach((input: Element) => {
                        if (!input.classList.contains('tw-text-t-base')) {
                            input.classList.add('tw-text-t-base');
                        }
                        if (!input.classList.contains('tw-text-t-primary')) {
                            input.classList.add('tw-text-t-primary');
                        }
                    });
                }, 0);
            } catch (error) {
                logger.error('Error compiling AngularJS template:', error);
                // Fallback: display the template as plain HTML
                if (currentElement) {
                    currentElement.innerHTML = template;
                }
            }
        };

        compileTemplate();

        return () => {
            cancelled = true;
            scopeRef.current?.$destroy();
            scopeRef.current = null;
        };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, resolvedFieldPath, rerenderKey]);

    // Watch for dynamically added option spans and inputs, and add Tailwind classes
    useEffect(() => {
        if (!containerRef.current) return;

        const addClassesToElements = () => {
            // Add tw-text-t-base and tw-text-t-primary to option-label spans
            const optionLabelSpans = containerRef.current?.querySelectorAll('span.option-label');
            optionLabelSpans?.forEach((span: Element) => {
                if (!span.classList.contains('tw-text-t-base')) {
                    span.classList.add('tw-text-t-base');
                }
                if (!span.classList.contains('tw-text-t-primary')) {
                    span.classList.add('tw-text-t-primary');
                }
            });
            // Add tw-text-t-sm and tw-text-t-primary to option-key spans
            const optionKeySpans = containerRef.current?.querySelectorAll('span.option-key');
            optionKeySpans?.forEach((span: Element) => {
                if (!span.classList.contains('tw-text-t-sm')) {
                    span.classList.add('tw-text-t-sm');
                }
                if (!span.classList.contains('tw-text-t-primary')) {
                    span.classList.add('tw-text-t-primary');
                }
            });
            // Add tw-text-t-base and tw-text-t-primary to all input elements
            const inputs = containerRef.current?.querySelectorAll('input');
            inputs?.forEach((input: Element) => {
                if (!input.classList.contains('tw-text-t-base')) {
                    input.classList.add('tw-text-t-base');
                }
                if (!input.classList.contains('tw-text-t-primary')) {
                    input.classList.add('tw-text-t-primary');
                }
            });
        };

        // Initial check
        addClassesToElements();

        // Watch for changes in the DOM
        const observer = new MutationObserver(() => {
            addClassesToElements();
        });

        observer.observe(containerRef.current, {
            childList: true,
            subtree: true,
            attributes: false
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <div ref={containerRef} className={className}></div>
    );
};

export { AngularJSTemplateRenderer };
